import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router-dom';

/**
 * Warns before losing unsaved form data: the browser dialog on tab close/reload, and a caller
 * rendered confirmation for in-app navigation. Requires a data router (see `index.tsx`).
 */
export function useUnsavedChangesGuard(dirty: boolean) {
  // Navigations we trigger ourselves right after saving must not be blocked by the state that a
  // pending re-render has not cleared yet.
  const bypassRef = useRef(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        dirty && !bypassRef.current && currentLocation.pathname !== nextLocation.pathname,
      [dirty]
    )
  );

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  /** Wraps in-page actions that discard changes (e.g. switching tabs). */
  const guard = useCallback((action: () => void) => {
    if (!dirty) {
      action();
      return;
    }
    setPendingAction(() => action);
  }, [dirty]);

  const runWithoutGuard = useCallback(<T,>(action: () => T): T => {
    bypassRef.current = true;
    try {
      return action();
    } finally {
      setTimeout(() => { bypassRef.current = false; }, 0);
    }
  }, []);

  const confirmLeave = useCallback(() => {
    if (pendingAction) {
      const action = pendingAction;
      setPendingAction(null);
      action();
      return;
    }
    blocker.proceed?.();
  }, [pendingAction, blocker]);

  const cancelLeave = useCallback(() => {
    if (pendingAction) {
      setPendingAction(null);
      return;
    }
    blocker.reset?.();
  }, [pendingAction, blocker]);

  return {
    confirmOpen: pendingAction !== null || blocker.state === 'blocked',
    confirmLeave,
    cancelLeave,
    guard,
    runWithoutGuard,
  };
}
